import React from 'react'
import {
  flowMax,
  addDisplayName,
  addWrapper,
  addProps,
  addMemoBoundary,
} from 'ad-hok'
import {FC} from 'react'
import {get} from 'lodash'
import {addPropIdentityStabilization} from 'ad-hok-utils'

import {makeFormSchema, ExtractFormSchemaFields} from 'utils/form/schema'
import Form, {OnSubmitSuccessOptions} from 'components/Form'
import {addTranslationHelpers} from 'utils/i18n'
import {addClasses, makeClasses} from 'theme'
import Dialog from 'components/Dialog'
import DialogTitle from 'components/DialogTitle'
import DialogContent from 'components/DialogContent'
import DialogActions from 'components/DialogActions'
import Button from 'components/Button'
import {deductionSchema} from 'components/EditPersonForm/schema'
import SelectField from 'components/SelectField'
import FormRow from 'components/FormRow'
import CurrencyField from 'components/CurrencyField'
import {
  addSubmitForm,
  addFormikSubmitFormCallback,
} from 'utils/addFormikHoistedState'
import addHasSaved from 'utils/addHasSaved'
import {addFieldsFreshnessContext} from 'utils/form/context'
import {PersonFields_deductions} from 'graphql/deserializedTypes/PersonFields'

const classes = makeClasses(() => ({
  contentContainer: {
    width: 600,
  },
}))

export const deductionFormSchema = makeFormSchema({
  fields: {
    deduction: deductionSchema,
  },
})

interface Props {
  deduction?: PersonFields_deductions
  itemName?: string
  onSave: (
    options: OnSubmitSuccessOptions<
      ExtractFormSchemaFields<typeof deductionFormSchema>
    >
  ) => void
  open: boolean
  onClose: () => void
}

const DeductionDialog: FC<Props> = flowMax(
  addDisplayName('DeductionDialog'),
  addFieldsFreshnessContext,
  addProps(({fieldsFreshness, itemName}) => ({
    initialFieldsFreshness: {
      deduction: itemName ? get(fieldsFreshness, itemName, {}) : {},
    },
  })),
  addPropIdentityStabilization('initialFieldsFreshness'),
  addMemoBoundary([
    'deduction',
    'onSave',
    'open',
    'onClose',
    'initialFieldsFreshness',
  ]),
  addSubmitForm,
  addHasSaved('onSave'),
  addClasses(classes),
  addTranslationHelpers,
  addWrapper(
    (
      render,
      {
        classes,
        open,
        onClose,
        deduction,
        onSave,
        submitForm,
        hasSaved,
        initialFieldsFreshness,
        t,
      }
    ) => (
      <Dialog
        open={open}
        onClose={onClose}
        data-testid={`deduction-dialog-${deduction?.id ?? 'new'}`}
        scroll="paper"
      >
        <DialogTitle>{t('deductionDialog.title')}</DialogTitle>
        <DialogContent className={classes.contentContainer} dividers>
          <Form
            name="deductionForm"
            schema={deductionFormSchema}
            onSubmitSuccess={onSave}
            initialValues={deduction && {deduction}}
            initialFieldsFreshness={initialFieldsFreshness}
            initialTouched={{deduction: {startMonth: true}}}
            shouldTrackFieldsUpdatedAt
          >
            {render()}
          </Form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            {t('deductionDialog.cancel')}
          </Button>
          <Button
            onClick={submitForm}
            variant="contained"
            color="primary"
            disabled={hasSaved}
          >
            {t('deductionDialog.save')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  ),
  addFormikSubmitFormCallback,
  () => (
    <>
      <SelectField name="deduction.deductionType" />
      <FormRow variant="fourColumn_1_1_1_1_true">
        <SelectField name="deduction.startMonth" lazyIcon />
        <SelectField name="deduction.startYear" lazyIcon />
        <SelectField name="deduction.endMonth" lazyIcon />
        <SelectField name="deduction.endYear" lazyIcon />
      </FormRow>
      <FormRow variant="twoColumn_1_1_true">
        <CurrencyField name="deduction.amount" lazyIcon />
        <SelectField name="deduction.deductionFrequency" lazyIcon />
      </FormRow>
    </>
  )
)

export default DeductionDialog
